<template>
  <transition>
    <div :class="menuClassName">
      <div class="rt-sidebar-menu-header">
        <div
          class="rt-sidebar-menu-header-logo"
          @click="logoClickHandler"
        >
          <slot name="header-logo">
            <div class="rt-sidebar-menu-header-logo-img">
              <slot name="logo">
                <rt-icon
                  :size="40"
                  :img-src="logo"
                ></rt-icon>
              </slot>
            </div>
            <div class="rt-sidebar-menu-header-logo-text">
              <slot name="logo-text">
                <img :src="logoText" />
              </slot>
            </div>
          </slot>
        </div>
        <div
          v-if="customModelPosition === 'top'"
          class="rt-sidebar-menu-header-custom-model"
        >
          <slot name="custom-model"></slot>
        </div>
        <div
          v-if="search"
          class="rt-sidebar-menu-header-search"
        >
          <rt-search
            v-if="!isDropdownSearch"
            v-model="searchValue"
            :placeholder="$tx('请搜索...', 'xpc.search.pleaseSearch')"
            type="dark"
            :more="false"
            :border="false"
            @on-search="searchClick"
          />
          <rt-input
            v-else
            v-model="searchValue"
            type="autocomplete"
            popper-class="rt-sidebar-menu-header-popper"
            :trigger-on-focus="false"
            :show-suffix-icon="false"
            :highlight-first-item="true"
            :clearable="true"
            :clear-icon-right="8"
            :value-key="innerAlias.title"
            :placeholder="$tx('请搜索...', 'xpc.search.pleaseSearch')"
            :fetch-suggestions="queryFilter"
            @select="handleSelect"
          >
            <rt-icon
              slot="prefix"
              size="18"
              name="rt-base-lookup"
            />
          </rt-input>
        </div>
        <div
          v-if="customModelPosition === 'bottom'"
          class="rt-sidebar-menu-header-custom-model"
        >
          <slot name="custom-model"></slot>
        </div>
      </div>
      <div class="rt-sidebar-menu-body">
        <rt-sidebar-menu-body
          :data-source="filterMenuList"
          :collapse="innerCollapse"
          :theme="theme"
          :default-active="innerDefaultAction"
          :default-openeds="innerOpenedsMenu"
          :alias="innerAlias"
          :search-value="!isDropdownSearch ? searchValue : ''"
          :active-index="activeIndex"
          @select="selectHandler"
          @open="openHandler"
          @close="closeHandler"
        />
      </div>
      <div
        v-if="$slots.footer"
        class="rt-sidebar-menu-footer"
      >
        <slot name="footer"></slot>
      </div>
      <div
        class="rt-sidebar-menu-collapse"
        :class="innerCollapse ? 'arrow-right' : 'arrow-left'"

        @click="triggerCollapse"
      >
        <rt-icon
          :size="10"
          name="rt-base-arrow-right-lined"
        ></rt-icon>
      </div>
    </div>
  </transition>
</template>

<script>
import { cloneDeep } from 'lodash';
import RtSidebarMenuBody from './rtSidebarMenuBody.vue';
import RtIcon from '../../icon/rtIcon';

export default {
  name: 'RtSidebarMenu',
  components: { RtSidebarMenuBody, RtIcon },
  props: {
    theme: {
      type: String,
      default: 'dark',
      validator: theme => {
        return ['dark', 'light'].includes(theme);
      }
    },
    logo: {
      type: [String]
    },
    logoText: {
      type: String
    },
    search: {
      type: Boolean,
      default: true
    },
    isDropdownSearch: {
      type: Boolean,
      default: true
    },
    collapse: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: Boolean,
      default: true
    },
    customModelPosition: {
      type: String,
      default: 'top',
      validator: position => {
        return ['top', 'bottom'].includes(position);
      }
    },
    defaultActive: {
      type: String
    },
    defaultOpeneds: {
      type: Array
    },
    dataSource: {
      type: Array
    },
    fetch: {
      type: Function
    },
    alias: {
      type: Object,
      default () {
        return {};
      }
    }
  },
  data () {
    return {
      // allMenuChildren: [],
      menuList: [],
      searchValue: '',
      innerCollapse: this.collapse,
      innerDefaultAction: this.defaultActive,
      innerOpenedsMenu: this.defaultOpeneds,
      menuIconSrcPromise: [],
      menuIconSrcPromiseResults: [],
      activeIndex: '',
      allQueryList: []
    };
  },
  computed: {
    menuClassName () {
      const className = ['rt-sidebar-menu', `rt-sidebar-menu-${this.theme}`];
      if (this.innerCollapse) {
        className.push('is-collapse');
      }
      return className;
    },
    filterMenuList () {
      if (!this.isDropdownSearch && this.searchValue && !this.collapse) {
        const list = this.getFilterMenuListByMenuList(this.menuList);
        if (list && list.length > 0) {
          const openMenus = [];
          this.findOpenMenus(list, openMenus);
          this.innerOpenedsMenu = openMenus;
        }
        return list;
      }
      return this.menuList;
    },
    innerAlias () {
      return {
        title: 'title',
        desc: 'desc',
        path: 'path',
        id: 'id',
        iconName: 'iconName',
        iconSrc: 'iconSrc',
        children: 'children',
        ...this.alias
      };
    },
    queryMenuList () {
      const newList = cloneDeep(this.menuList);
      return this.getDropdownMenuList(newList);
    }
  },
  watch: {
    dataSource (val) {
      this.prevMenuListHandler(val);
    },
    collapse (val) {
      this.innerCollapse = val;
    },
    defaultActive (val) {
      this.innerDefaultAction = val;
    }
  },
  mounted () {
    if (this.fetch && typeof this.fetch === 'function') {
      this.loadMenuFetch();
    }
    if (this.dataSource && this.dataSource.length > 0) {
      this.prevMenuListHandler(this.dataSource);
    }
    this.allQueryList = [];
  },
  methods: {
    expanded () {
      this.innerCollapse = false;
      this.$emit('collapse', this.innerCollapse);
    },
    collapsed () {
      this.innerCollapse = false;
      this.$emit('collapse', this.innerCollapse);
    },
    triggerCollapse () {
      this.innerCollapse = !this.innerCollapse;
      this.$emit('collapse', this.innerCollapse);
    },
    selectHandler (index, data, item) {
      this.$emit('select', index, data, item);
    },
    openHandler (index, data) {
      this.$emit('open', index, data);
    },
    closeHandler (index, data) {
      this.$emit('close', index, data);
    },
    logoClickHandler () {
      this.$emit('logo-click');
    },
    searchClick () {
      if (this.innerCollapse) {
        this.innerCollapse = false;
        this.$emit('collapse', this.innerCollapse);
      }
    },
    loadMenuFetch () {
      this.fetch().then(res => {
        this.prevMenuListHandler(res);
      });
    },
    findPathBayMenuList (list, path) {
      if (!list || list.length <= 0) {
        return;
      }
      for (let i = 0; i < list.length; i++) {
        const menu = list[i];
        if (menu[this.innerAlias.path] && menu[this.innerAlias.path] === path) {
          return menu;
        } else {
          const activeMenu = this.findPathBayMenuList(
            menu[this.innerAlias.children],
            path
          );
          if (activeMenu) {
            return activeMenu;
          }
        }
      }
    },
    getMenuActiveId () {
      if (this.$route && this.$route.path) {
        const path = this.$route.path.slice(1);
        const activeMenu = this.findPathBayMenuList(this.menuList, path);
        if (activeMenu && activeMenu[this.innerAlias.id]) {
          this.innerDefaultAction = activeMenu[this.innerAlias.id];
        }
      }
    },
    getFilterMenuListByMenuList (list) {
      let newMenus;
      const newList = cloneDeep(list);
      if (newList && newList.length > 0) {
        newMenus = newList.filter(li => {
          if (
            li[this.innerAlias.children] &&
            li[this.innerAlias.children].length > 0
          ) {
            const children = this.getFilterMenuListByMenuList(
              li[this.innerAlias.children]
            );
            if (children && children.length > 0) {
              li[this.innerAlias.children] = children;
              return true;
            }
            return false;
          }
          const title = li[this.innerAlias.title].toString().toLocaleLowerCase();
          const value = this.searchValue.toLocaleLowerCase();
          return title.includes(value);
        });
      }
      return newMenus;
    },
    findOpenMenus (list, openMenus) {
      if (list && list.length > 0) {
        list.forEach(li => {
          if (li) {
            if (li[this.innerAlias.children] && li[this.innerAlias.children].length > 0) {
              openMenus.push(li[this.innerAlias.id]);
              this.findOpenMenus(li[this.innerAlias.children], openMenus);
            }
          }
        });
      }
    },
    prevMenuListHandler (list) {
      const newList = cloneDeep(list);
      this.findMenuIconSrc(newList);
      if (this.menuIconSrcPromise.length > 0) {
        const promise = new Promise((resolve) => {
          const promiseList = [];
          const len = this.menuIconSrcPromise.length;
          let count = len;

          this.menuIconSrcPromise.forEach((childPromise, i) => {
            childPromise.fetch.then(res => {
              promiseList[i] = { status: 'fufilled', value: res };
            }).catch(err => {
              promiseList[i] = { status: 'rejected', value: err };
            })
              .finally(() => {
                count--;
                if (count === 0) {
                  resolve(promiseList);
                }
              })
          });
        });
        promise.then(res => {
          return res.map((li, i) => {
            if (li.status === 'fufilled') {
              return {
                id: this.menuIconSrcPromise[i].id,
                iconSrc: li.value
              }
            }
            return { id: this.menuIconSrcPromise[i].id }
          }).filter(v => v)
        }).then(res => {
          this.updateMenuListIconSrc(newList, res);
          this.menuList = newList;

          if (!this.defaultActive) {
            this.getMenuActiveId();
          }
        });
      } else {
        this.menuList = newList;

        if (!this.defaultActive) {
          this.getMenuActiveId();
        }
      }
    },
    findMenuIconSrc (list) {
      list.forEach(li => {
        if (li[this.innerAlias.iconSrc]) {
          this.menuIconSrcPromise.push({
            id: li[this.innerAlias.id],
            fetch: this.getIconSrcFetch(li[this.innerAlias.iconSrc])
          });
        }
        if (li[this.innerAlias.children] && li[this.innerAlias.children].length > 0) {
          this.findMenuIconSrc(li[this.innerAlias.children]);
        }
      });
    },
    updateMenuListIconSrc (list, iconSrcResults) {
      list.forEach(li => {
        if (li[this.innerAlias.iconSrc]) {
          const d = iconSrcResults.find(data => data.id === li[this.innerAlias.id]);
          if (d) {
            li[this.innerAlias.iconSrc] = d.iconSrc;
          }
        }
        if (li[this.innerAlias.children] && li[this.innerAlias.children].length > 0) {
          this.updateMenuListIconSrc(li[this.innerAlias.children], iconSrcResults);
        }
      });
    },
    /**
     *
     * @param {String} path
     */
    getIconSrcFetch (path) {
      const reg = /(http|https|ftp):\/\//;
      const isUrlPath = reg.test(path);
      if (isUrlPath) {
        return fetch(path, {
          headers: {
            Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
            'Content-Type': 'image/png'
          }
        }).then(res => {
          return res.arrayBuffer();
        }).then(res => {
          let str = String.fromCharCode(...new Uint8Array(res));
          str = window.btoa(str);
          if (str) {
            return `data:image/png;base64,${str}`;
          }
          return ''
        })
      }
      return Promise.resolve(path);
    },
    getDropdownMenuList (list) {
      if (list && list.length) {
        list.forEach(item => {
          if (item[this.innerAlias.children] && item[this.innerAlias.children].length) {
            this.getDropdownMenuList(item[this.innerAlias.children]);
          } else {
            this.allQueryList.push(item);
          }
        })
      }
      return this.allQueryList;
    },
    queryFilter (filterValue, cb) {
      filterValue = (filterValue || '').trim().toLowerCase();

      let queryList = [];
      if (filterValue) {
        queryList = this.queryMenuList.filter(
          item => item[this.innerAlias.title].toString().toLowerCase().includes(filterValue)
        )
      }
      cb(queryList);
    },
    handleSelect (item) {
      this.$emit('select', item[this.innerAlias.id], item);
    }
  }
};
</script>

<style></style>



// WEBPACK FOOTER //
// rtSidebarMenu.vue?6c9b75da